import React, { useEffect, useState } from 'react'
import FormPage from './FormPage'
import FormFields from './FormFields'
import FormDesign from './FormDesign'
import FormFieldCustomization from './FormFieldCustomization'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { PageTitle } from '../../../layout/core'
import { useMutation, useQuery } from '@apollo/client'
import { GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID } from '../../../graphql/query'
import { isEqual } from 'lodash'
import ReactModal from 'react-modal'
import SaveFormBuilder from './SaveFormBuilder'
import { toast } from 'react-toastify'
import { UPDATE_FORM_BUILDER_BY_FORM_STRING_ID } from '../../../graphql/mutation'
import { resolveFormPages } from '../../../pages/processAutomation/formBuilder/formBuilderModel/formPreviewUtils'
import { ensurePrefillFieldSettings, usesPrefillEditFieldsMode } from './prefillFieldSettingsUtils'
import { ensureUniqueFormFieldIds } from './tableFieldUtils'

function recHasProp(obj: any, prop: any) {
  for (let p in obj) {
    if (obj.hasOwnProperty(p)) {
      if (p === prop) {
        return obj
      } else if (obj[p] instanceof Object && recHasProp(obj[p], prop)) {
        return obj[p]
      }
    }
  }
  return null
}

const EditFormBuilder: React.FC = () => {
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [formElements, setFormElements] = useState<any[]>([[]])
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [selectedField, setSelectedFields] = useState<any>(0)
  const [selectedTableColumnIndex, setSelectedTableColumnIndex] = useState<number | null>(null)
  const [selectedTableRowIndex, setSelectedTableRowIndex] = useState<number | null>(null)
  const [showSaveButton, setShowSaveButton] = useState(false)
  const [formJson, setFormJson] = useState<any>()
  const [ThemeTextColor, setThemeTextColor] = useState('#000000')
  const [togglerFieldIndex, setTogglerFieldIndex] = useState(-1)
  const [subFormFieldIndexes, setSubFormFieldIndexes] = useState([])
  const [showTimeModal, setShowTimeModal] = useState(false)
  const [isFormTimed, setIsFormTimed] = useState(formJson?.isFormTime || false)
  const [formTimeLimit, setFormTimeLimit] = useState(formJson?.isTime || '00:00:00')

  const tabTitles = ['Form Fields']
  const params = useParams()
  const uuid = params.uuid
  const [isSaveModalOpen, setIsSaveModalOpen] = useState<boolean>(false)
  const navigate = useNavigate()
  const { loading, error, data } = useQuery(GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID, {
    variables: {
      input: {
        formUuid: uuid
      }
    },
    context: {
      headers: {
        'access-control-allow-methods': 'Form-Builder'
      }
    }
  })

  const [SaveFormJson] = useMutation(UPDATE_FORM_BUILDER_BY_FORM_STRING_ID, {
    refetchQueries: [
      {
        query: GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID,
        variables: {
          input: {
            formUuid: uuid
          }
        },
        context: {
          headers: {
            'access-control-allow-methods': 'Form-Builder'
          }
        }
      }
    ],
    context: {
      headers: {
        'access-control-allow-methods': 'Form-Builder'
      }
    }
  })

  const handleTimeModalSave = async () => {
    try {
      let updatedAllFormElements = [...formElements]

      if (isFormTimed) {
        updatedAllFormElements = updatedAllFormElements.map((pageElements) =>
          Array.isArray(pageElements)
            ? pageElements.map((element) => ({
                ...element,
                isTime: false
              }))
            : pageElements
        )
      }

      setFormElements(updatedAllFormElements)

      const inputRes = await SaveFormJson({
        variables: {
          input: {
            formUuid: uuid,
            cardStyle: formJson?.cardStyle,
            cardBackground: formJson?.cardBackground,
            isFormTime: isFormTimed,
            isTime: formTimeLimit,
            featuresToUpdate: {
              formData: updatedAllFormElements
            }
          }
        }
      })

      if (inputRes.data.updateFormBuilderByFormStringId.message) {
        toast.success('Timer settings saved successfully!')
      }

      setShowTimeModal(false)
    } catch (error) {
      toast.error('Failed to save timer settings. Please try again.')
      console.error('Error saving timer settings:', error)
    }
  }
  //   {
  //   try {
  //     const updatedFormJson = {
  //       ...formJson,
  //       isFormTime: isFormTimed,
  //       isTime: formTimeLimit
  //     }

  //     await SaveFormJson({
  //       variables: {
  //         input: {
  //           formUuid: uuid,
  //           cardStyle: updatedFormJson?.cardStyle,
  //           cardBackground: updatedFormJson?.cardBackground,
  //           isFormTime: isFormTimed,
  //           isTime: formTimeLimit,
  //           featuresToUpdate: {
  //             formData: formElements
  //           }
  //         }
  //       }
  //     })

  //     toast.success('Timer settings saved successfully!')
  //     setShowTimeModal(false)
  //   } catch (err) {
  //     console.error('Failed to save time settings:', err)
  //     toast.error('Failed to save timer settings')
  //   }
  // }

  const autoSaveChanges = async (updatedFormElements: any[]) => {
    try {
      const uniqueFormData = ensureUniqueFormFieldIds(
        Array.isArray(updatedFormElements?.[0]) ? updatedFormElements : [updatedFormElements]
      )
      await SaveFormJson({
        variables: {
          input: {
            formUuid: uuid,
            cardStyle: formJson?.cardStyle,
            cardBackground: formJson?.cardBackground,
            featuresToUpdate: {
              formData: uniqueFormData
            }
          }
        }
      })
      return true
    } catch (err) {
      toast.error('Auto-save failed. Please save manually.')
      console.error('Auto-save error:', err)
      return false
    }
  }

  const addNewPageWithAutoSave = async () => {
    const updatedFormElements = [...formElements, []]
    setFormElements(updatedFormElements)

    const saveSuccess = await autoSaveChanges(updatedFormElements)

    if (saveSuccess) {
      const newPageIndex = updatedFormElements.length - 1
      setCurrentPageIndex(newPageIndex)
      setSelectedFields(null)
      setShowSaveButton(false)
    } else {
      setShowSaveButton(true)
    }
  }

  useEffect(() => {
    if (showTimeModal) {
      setIsFormTimed(formJson?.isFormTime || false)
      setFormTimeLimit(formJson?.isTime || '00:00:00')
    }
  }, [showTimeModal, formJson])

  useEffect(() => {
    if (
      data?.getAllFormBuilderByBgAccountId?.data &&
      data?.getAllFormBuilderByBgAccountId?.data.length !== undefined
    ) {
      if (data?.getAllFormBuilderByBgAccountId?.data[0].formData.length !== undefined) {
        const tempFormJSON = data?.getAllFormBuilderByBgAccountId?.data[0].formData
        const isPrefillForm = data?.getAllFormBuilderByBgAccountId?.data[0]?.isPrefillForm === true

        const applyFieldDefaults = (field: any, _index: number) => {
          let tempfield = { ...field }
          if (recHasProp(field, 'includeOnCondition') == null) {
            if (Object.isExtensible(tempfield)) {
              tempfield = {
                ...tempfield,
                includeOnCondition: {
                  conditionActive: false,
                  basedOnElementOfIndex: null,
                  forValue: 'undefined'
                }
                // Do NOT overwrite fieldId with page-local index — that creates cross-page collisions.
              }
            }
          }
          return isPrefillForm ? ensurePrefillFieldSettings(tempfield) : tempfield
        }

        // Check if formData has pages structure or is flat array
        if (Array.isArray(tempFormJSON[0])) {
          // Already has pages structure
          const renewedFormJSON = ensureUniqueFormFieldIds(
            tempFormJSON.map((page: any[]) =>
              page.map((field: any, index: number) => applyFieldDefaults(field, index))
            )
          )
          setFormElements(renewedFormJSON)
        } else {
          // Convert flat array to pages structure (backward compatibility)
          const renewedFormJSON = ensureUniqueFormFieldIds([
            tempFormJSON.map((field: any, index: number) => applyFieldDefaults(field, index))
          ])
          setFormElements(renewedFormJSON)
        }
      } else {
        setFormElements([[]]) // Initialize with empty first page
      }
      setFormJson(data?.getAllFormBuilderByBgAccountId?.data[0])
    }
  }, [data])

  useEffect(() => {
    if (isEqual(formJson, formElements)) {
      setShowSaveButton(false)
    } else {
      setShowSaveButton(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formElements])

  useEffect(() => {
    /* this formJson dependent side effect would enable/disable save button if there's a difference of styles/design that is observed */
    const CardstyleChanges = data?.getAllFormBuilderByBgAccountId?.data[0]

    if (isEqual(CardstyleChanges, formJson)) {
      setShowSaveButton(false)
    } else {
      setShowSaveButton(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formJson])

  if (loading) {
    return <>Loading....</>
  }
  if (error) {
    console.log(error)
  }

  const handlePageChange = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex)
    setSelectedFields(null)
  }

  const formBuilderData = data?.getAllFormBuilderByBgAccountId?.data[0].formData || []

  const customModalStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-30%, -60%)',
      width: '350px'
    }
  }

  const openSaveModal = () => {
    setIsSaveModalOpen(true)
  }

  const afterOpenSaveModal = () => {
    // subtitle.style.color = '#f00'
  }

  const closeSaveModal = () => {
    setIsSaveModalOpen(false)
  }

  const handleBack = () => {
    navigate('/automation/process-automation/form-builder')
  }

  const handleImportFormStructure = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result))
        const pages = resolveFormPages(parsed?.formData ?? parsed)
        if (!pages.some((page) => page.length > 0)) {
          toast.error('Invalid form JSON. Use formData from the form builder export.')
          return
        }
        setFormElements(pages)
        setCurrentPageIndex(0)
        setSelectedFields(null)
        setShowSaveButton(true)
        toast.success('Form structure loaded. Click Save to apply changes.')
      } catch {
        toast.error('Could not parse the JSON file.')
      }
    }
    reader.readAsText(file)
    event.target.value = ''
  }

  return (
    <>
      <PageTitle breadcrumbs={[]} children={undefined}></PageTitle>
      <section className='row p-4'>
        <div
          className='col-2 d-flex flex-column card rounded border border-secondary scroll-y h-800px'
          style={{ position: 'sticky', top: '0' }}
        >
          <ul className='nav nav-stretch nav-line-tabs border-transparent d-flex justify-content-center flex-nowrap mb-3'>
            {tabTitles.map((title, index) => (
              <li className='nav-item' key={title}>
                <span
                  className={`nav-link cursor-pointer text-uppercase fs-7 fw-bold text-muted pb-2 ${
                    activeTabIndex === index ? 'active' : ''
                  }`}
                  onClick={() => {
                    setActiveTabIndex(index)
                  }}
                  role='tab'
                >
                  {title}
                </span>
              </li>
            ))}
          </ul>

          {activeTabIndex === 0 && (
            <FormFields
              formElements={formElements}
              setFormElements={setFormElements}
              selectedField={selectedField}
              ThemeTextColor={ThemeTextColor}
              currentPageIndex={currentPageIndex}
              addNewPage={addNewPageWithAutoSave}
            />
          )}
          {/* <div className='scroll-y'>
            {activeTabIndex === 1 && (
              <FormDesign
                formElements={formElements}
                setFormElements={setFormElements}
                setFormJson={setFormJson}
                formJson={formJson}
                setThemeTextColor={setThemeTextColor}
              />
            )}
          </div> */}
        </div>

        <div
          className='col border border-warning mx-3 rounded p-0 scroll-y h-800px position-relative '
          style={{
            backgroundImage: `url(${formJson?.cardBackground || ''})`
          }}
        >
          <div className='d-flex flex-column'>
            <span role='button' className='mt-4 ms-4 text-hover-primary' onClick={handleBack}>
              <i className='bi bi-arrow-left text-black' style={{ fontSize: '30px' }}></i>
            </span>

            {formElements.length > 1 && (
              <div className='d-flex justify-content-center mt-3 mb-3 position-relative'>
                <nav>
                  <ul className='pagination pagination-sm'>
                    <div className='d-flex justify-content-between align-items-center mt-3 mb-3 px-4'>
                      <nav>
                        <ul className='pagination pagination-sm mb-0'>
                          <li className={`page-item ${currentPageIndex === 0 ? 'disabled' : ''}`}>
                            <button
                              className='page-link'
                              onClick={() => handlePageChange(currentPageIndex - 1)}
                            >
                              Prev
                            </button>
                          </li>

                          {(() => {
                            const totalPages = formElements.length
                            const startPage = Math.max(
                              0,
                              Math.min(currentPageIndex - 1, totalPages - 3)
                            )
                            const endPage = Math.min(totalPages, startPage + 3)

                            const pageItems = []
                            for (let i = startPage; i < endPage; i++) {
                              pageItems.push(
                                <li
                                  key={i}
                                  className={`page-item ${currentPageIndex === i ? 'active' : ''}`}
                                >
                                  <button className='page-link' onClick={() => handlePageChange(i)}>
                                    {i + 1}
                                  </button>
                                </li>
                              )
                            }
                            return pageItems
                          })()}

                          <li
                            className={`page-item ${
                              currentPageIndex === formElements.length - 1 ? 'disabled' : ''
                            }`}
                          >
                            <button
                              className='page-link'
                              onClick={() => handlePageChange(currentPageIndex + 1)}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </ul>
                </nav>
              </div>
            )}

            <div
              className='position-absolute end-0 p-3 m-3 d-flex align-items-end gap-3'
              style={{ zIndex: 0 }}
            >
              <label
                className='mb-0'
                role='button'
                title='Import form structure from JSON (formData only)'
              >
                <i
                  className='bi bi-file-earmark-arrow-up text-black'
                  style={{ fontSize: '28px' }}
                />
                <input
                  type='file'
                  accept='application/json,.json'
                  className='d-none'
                  onChange={handleImportFormStructure}
                />
              </label>
              <span
                className='me-4'
                role='button'
                onClick={() => setShowTimeModal(true)}
                title='Set time limit to complete form'
              >
                <i className='bi bi-clock text-black' style={{ fontSize: '30px' }}></i>
              </span>
              <Link to={`/automation/process-automation/form-builder/share-form/${uuid}`}>
                <span role='button'>
                  <i className='bi bi-share-fill text-black' style={{ fontSize: '30px' }}></i>
                </span>
              </Link>
            </div>

            {showTimeModal && (
              <div
                className='modal fade show d-block'
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                onClick={() => setShowTimeModal(false)}
              >
                <div
                  className='modal-dialog modal-dialog-centered'
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className='modal-content'>
                    <div className='modal-header'>
                      <h5 className='modal-title'>Form Time Limit</h5>
                      <button
                        type='button'
                        className='btn-close'
                        onClick={() => setShowTimeModal(false)}
                      ></button>
                    </div>
                    <div className='modal-body'>
                      <p>
                        Enable the toggle if you want to add a time limit for filling the form. The
                        form will be auto-submitted once the timer expires.
                      </p>
                      <div className='form-check form-switch my-3'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='formTimeToggle'
                          value={isFormTimed}
                          checked={isFormTimed}
                          onChange={(e) => setIsFormTimed(e.target.checked)}
                        />
                        <label className='form-check-label' htmlFor='formTimeToggle'>
                          Enable form timer
                        </label>
                      </div>

                      {isFormTimed && (
                        <div className='mt-3'>
                          <label htmlFor='timeInput' className='form-label'>
                            Select Time (HH:MM:SS)
                          </label>
                          <input
                            type='time'
                            step='1'
                            className='form-control'
                            id='timeInput'
                            value={formTimeLimit}
                            onChange={(e) => setFormTimeLimit(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                    <div className='modal-footer'>
                      <button
                        type='button'
                        className='btn btn-light'
                        onClick={() => setShowTimeModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type='button'
                        className='btn btn-primary'
                        onClick={handleTimeModalSave}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <FormPage
            formElements={formElements[currentPageIndex] || []}
            setFormElements={setFormElements}
            isFormTimed={isFormTimed}
            setSelectedFields={setSelectedFields}
            selectedField={selectedField}
            formJson={formJson}
            togglerFieldIndex={togglerFieldIndex}
            setTogglerFieldIndex={setTogglerFieldIndex}
            subFormFieldIndexes={subFormFieldIndexes}
            setSubFormFieldIndexes={setSubFormFieldIndexes}
            allFormElements={formElements}
            currentPageIndex={currentPageIndex}
            setCurrentPageIndex={setCurrentPageIndex}
            isPrefillForm={formJson?.isPrefillForm === true}
            selectedTableColumnIndex={selectedTableColumnIndex}
            onSelectTableColumn={setSelectedTableColumnIndex}
            selectedTableRowIndex={selectedTableRowIndex}
            onSelectTableRow={setSelectedTableRowIndex}
          />
        </div>

        <div className='col-3 d-flex flex-column card rounded border border-secondary scroll-y h-800px'>
          {showSaveButton && (
            <>
              <button className='btn btn-primary w-100 mt-5' onClick={openSaveModal}>
                Save
              </button>
              <hr />
            </>
          )}
          <FormFieldCustomization
            formElements={formElements[currentPageIndex] || []}
            setFormElements={setFormElements}
            selectedField={selectedField}
            isFormTimed={isFormTimed}
            setSelectedFields={setSelectedFields}
            formBuilderData={formBuilderData}
            showSaveButton={showSaveButton}
            togglerFieldIndex={togglerFieldIndex}
            setTogglerFieldIndex={setTogglerFieldIndex}
            subFormFieldIndexes={subFormFieldIndexes}
            setSubFormFieldIndexes={setSubFormFieldIndexes}
            allFormElements={formElements}
            currentPageIndex={currentPageIndex}
            isPrefillForm={formJson?.isPrefillForm === true}
            selectedTableColumnIndex={selectedTableColumnIndex}
            onSelectTableColumn={setSelectedTableColumnIndex}
            selectedTableRowIndex={selectedTableRowIndex}
            onSelectTableRow={setSelectedTableRowIndex}
          />
        </div>
      </section>
      <ReactModal
        isOpen={isSaveModalOpen}
        onAfterOpen={afterOpenSaveModal}
        onRequestClose={closeSaveModal}
        style={customModalStyles}
        contentLabel='Save'
      >
        <SaveFormBuilder
          closeSaveModal={closeSaveModal}
          formElements={formElements}
          formJson={formJson}
          setFormJson={setFormJson}
        />
      </ReactModal>
    </>
  )
}

export default EditFormBuilder
